import React, { createContext, useContext, useEffect, useMemo, ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { de } from '../translations/de';
import { en } from '../translations/en';
import { es } from '../translations/es';
import { ru } from '../translations/ru';
import {
  Lang,
  LANGUAGES,
  DEFAULT_LANG,
  langFromPath,
  findRouteByPath,
  localizedPath,
} from '../seo/routes';

type Language = Lang;
type Translations = Record<string, any>;

interface LanguageContextType {
  language: Language;
  translations: Translations;
  setLanguage: (language: Language) => void;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

interface LanguageProviderProps {
  children: ReactNode;
}

const TRANSLATIONS: Record<Language, Translations> = { de, en, es, ru };

/**
 * Ermittelt die bevorzugte Sprache aus gespeicherter Wahl bzw. Browser-Sprache.
 * Wird nur für den Einstieg ohne Sprach-Präfix gebraucht (z. B. "/"). Sobald ein
 * Präfix in der URL steht, ist die URL die alleinige Quelle der Wahrheit.
 */
export const detectPreferredLang = (): Language => {
  try {
    const saved = localStorage.getItem('language');
    if (saved && (LANGUAGES as string[]).includes(saved)) {
      return saved as Language;
    }
    const browser = navigator.language.split('-')[0];
    if ((LANGUAGES as string[]).includes(browser)) {
      return browser as Language;
    }
  } catch (error) {
    console.warn('Error reading preferred language:', error);
  }
  return DEFAULT_LANG;
};

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();

  // Sprache wird aus dem URL-Präfix abgeleitet; ohne Präfix gilt die Default-Sprache.
  const language: Language = langFromPath(location.pathname) ?? DEFAULT_LANG;

  const translations = useMemo(() => TRANSLATIONS[language] ?? de, [language]);

  // <html lang> setzen und Wahl für den nächsten prefixlosen Einstieg merken.
  useEffect(() => {
    document.documentElement.lang = language;
    try {
      localStorage.setItem('language', language);
    } catch (error) {
      console.warn('Error writing language preference:', error);
    }
  }, [language]);

  // Sprachwechsel = Navigation zur selben Seite in der Zielsprache (lokalisierter
  // Slug), Query-String und Hash bleiben erhalten.
  const setLanguage = (newLanguage: Language) => {
    const current = findRouteByPath(location.pathname);
    const key = current?.key ?? 'home';
    const target = `${localizedPath(key, newLanguage)}${location.search}${location.hash}`;
    navigate(target);
  };

  return (
    <LanguageContext.Provider value={{ language, translations, setLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
